import type { I18NMessages } from '@atlaskit/intl-messages-provider/types';
import { fetchMessagesForLocale as fetchAgentStudioPermissionSettingsLocale } from '@atlassian/agent-studio-permission-settings/i18n/fetchLocaleMessage';
import { fetchMessagesForLocale as fetchAgentStudioLocale } from '@atlassian/agent-studio/i18n/fetchLocaleMessage';
import { fetchMessagesForLocale as fetchAppBuilderStudioLocale } from '@atlassian/app-builder-studio/i18n/fetchLocaleMessage';
import { createFetchMessagesForLocale } from '@atlassian/atlassian-studio-common';
import { conversationAssistantI18n } from '@atlassian/conversation-assistant/i18n';
import { fetchMessagesForLocale as fetchSolutionTemplatesLocale } from '@atlassian/solution-templates/i18n/fetchLocaleMessage';
import { fetchMessagesForLocale as fetchSolutionArchitectUiComponentsLocale } from '@atlassian/studio-solution-architect-ui-components/i18n/fetchLocaleMessage';

import defaultMessages from '../en';

export const asyncLanguages = {
	cs: (): Promise<typeof import('../cs')> =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-foundations-i18n-cs" */ '../cs'
		),
	da: (): Promise<typeof import('../da')> =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-foundations-i18n-da" */ '../da'
		),
	de: (): Promise<typeof import('../de')> =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-foundations-i18n-de" */ '../de'
		),
	'en-GB': (): Promise<typeof import('../en_GB')> =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-foundations-i18n-en_GB" */ '../en_GB'
		),
	'en-ZZ': (): Promise<typeof import('../en_ZZ')> =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-foundations-i18n-en_ZZ" */ '../en_ZZ'
		),
	en: (): Promise<typeof import('../en')> =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-foundations-i18n-en" */ '../en'
		),
	es: (): Promise<typeof import('../es')> =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-foundations-i18n-es" */ '../es'
		),
	fi: (): Promise<typeof import('../fi')> =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-foundations-i18n-fi" */ '../fi'
		),
	fr: (): Promise<typeof import('../fr')> =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-foundations-i18n-fr" */ '../fr'
		),
	hu: (): Promise<typeof import('../hu')> =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-foundations-i18n-hu" */ '../hu'
		),
	it: (): Promise<typeof import('../it')> =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-foundations-i18n-it" */ '../it'
		),
	ja: (): Promise<typeof import('../ja')> =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-foundations-i18n-ja" */ '../ja'
		),
	ko: (): Promise<typeof import('../ko')> =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-foundations-i18n-ko" */ '../ko'
		),
	nb: (): Promise<typeof import('../nb')> =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-foundations-i18n-nb" */ '../nb'
		),
	nl: (): Promise<typeof import('../nl')> =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-foundations-i18n-nl" */ '../nl'
		),
	pl: (): Promise<typeof import('../pl')> =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-foundations-i18n-pl" */ '../pl'
		),
	'pt-BR': (): Promise<typeof import('../pt_BR')> =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-foundations-i18n-pt_BR" */ '../pt_BR'
		),
	ru: (): Promise<typeof import('../ru')> =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-foundations-i18n-ru" */ '../ru'
		),
	sv: (): Promise<typeof import('../sv')> =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-foundations-i18n-sv" */ '../sv'
		),
	th: (): Promise<typeof import('../th')> =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-foundations-i18n-th" */ '../th'
		),
	tr: (): Promise<typeof import('../tr')> =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-foundations-i18n-tr" */ '../tr'
		),
	uk: (): Promise<typeof import('../uk')> =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-foundations-i18n-uk" */ '../uk'
		),
	vi: (): Promise<typeof import('../vi')> =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-foundations-i18n-vi" */ '../vi'
		),
	zh_TW: (): Promise<typeof import('../zh_TW')> =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-foundations-i18n-zh_TW" */ '../zh_TW'
		),
	zh: (): Promise<typeof import('../zh')> =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-foundations-i18n-zh" */ '../zh'
		),
} as const;

const sitePickerAsyncLanguages = {
	cs: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-site-picker-i18n-cs" */ '@atlassian/atlassian-studio-site-picker/i18n/cs'
		),
	da: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-site-picker-i18n-da" */ '@atlassian/atlassian-studio-site-picker/i18n/da'
		),
	de: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-site-picker-i18n-de" */ '@atlassian/atlassian-studio-site-picker/i18n/de'
		),
	'en-GB': () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-site-picker-i18n-en_GB" */ '@atlassian/atlassian-studio-site-picker/i18n/en_GB'
		),
	'en-ZZ': () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-site-picker-i18n-en_ZZ" */ '@atlassian/atlassian-studio-site-picker/i18n/en_ZZ'
		),
	en: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-site-picker-i18n-en" */ '@atlassian/atlassian-studio-site-picker/i18n/en'
		),
	es: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-site-picker-i18n-es" */ '@atlassian/atlassian-studio-site-picker/i18n/es'
		),
	fi: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-site-picker-i18n-fi" */ '@atlassian/atlassian-studio-site-picker/i18n/fi'
		),
	fr: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-site-picker-i18n-fr" */ '@atlassian/atlassian-studio-site-picker/i18n/fr'
		),
	hu: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-site-picker-i18n-hu" */ '@atlassian/atlassian-studio-site-picker/i18n/hu'
		),
	it: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-site-picker-i18n-it" */ '@atlassian/atlassian-studio-site-picker/i18n/it'
		),
	ja: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-site-picker-i18n-ja" */ '@atlassian/atlassian-studio-site-picker/i18n/ja'
		),
	ko: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-site-picker-i18n-ko" */ '@atlassian/atlassian-studio-site-picker/i18n/ko'
		),
	nb: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-site-picker-i18n-nb" */ '@atlassian/atlassian-studio-site-picker/i18n/nb'
		),
	nl: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-site-picker-i18n-nl" */ '@atlassian/atlassian-studio-site-picker/i18n/nl'
		),
	pl: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-site-picker-i18n-pl" */ '@atlassian/atlassian-studio-site-picker/i18n/pl'
		),
	'pt-BR': () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-site-picker-i18n-pt_BR" */ '@atlassian/atlassian-studio-site-picker/i18n/pt_BR'
		),
	ru: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-site-picker-i18n-ru" */ '@atlassian/atlassian-studio-site-picker/i18n/ru'
		),
	sv: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-site-picker-i18n-sv" */ '@atlassian/atlassian-studio-site-picker/i18n/sv'
		),
	th: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-site-picker-i18n-th" */ '@atlassian/atlassian-studio-site-picker/i18n/th'
		),
	tr: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-site-picker-i18n-tr" */ '@atlassian/atlassian-studio-site-picker/i18n/tr'
		),
	uk: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-site-picker-i18n-uk" */ '@atlassian/atlassian-studio-site-picker/i18n/uk'
		),
	vi: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-site-picker-i18n-vi" */ '@atlassian/atlassian-studio-site-picker/i18n/vi'
		),
	zh_TW: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-site-picker-i18n-zh_TW" */ '@atlassian/atlassian-studio-site-picker/i18n/zh_TW'
		),
	zh: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/studio-site-picker-i18n-zh" */ '@atlassian/atlassian-studio-site-picker/i18n/zh'
		),
} as const;

const globalSideNavAsyncLanguages = {
	cs: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/global-side-nav-agents-i18n-cs" */ '@atlassian/agent-global-side-navigation/i18n/cs'
		),
	da: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/global-side-nav-agents-i18n-da" */ '@atlassian/agent-global-side-navigation/i18n/da'
		),
	de: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/global-side-nav-agents-i18n-de" */ '@atlassian/agent-global-side-navigation/i18n/de'
		),
	'en-GB': () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/global-side-nav-agents-i18n-en_GB" */ '@atlassian/agent-global-side-navigation/i18n/en_GB'
		),
	'en-ZZ': () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/global-side-nav-agents-i18n-en_ZZ" */ '@atlassian/agent-global-side-navigation/i18n/en_ZZ'
		),
	en: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/global-side-nav-agents-i18n-en" */ '@atlassian/agent-global-side-navigation/i18n/en'
		),
	es: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/global-side-nav-agents-i18n-es" */ '@atlassian/agent-global-side-navigation/i18n/es'
		),
	fi: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/global-side-nav-agents-i18n-fi" */ '@atlassian/agent-global-side-navigation/i18n/fi'
		),
	fr: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/global-side-nav-agents-i18n-fr" */ '@atlassian/agent-global-side-navigation/i18n/fr'
		),
	hu: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/global-side-nav-agents-i18n-hu" */ '@atlassian/agent-global-side-navigation/i18n/hu'
		),
	it: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/global-side-nav-agents-i18n-it" */ '@atlassian/agent-global-side-navigation/i18n/it'
		),
	ja: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/global-side-nav-agents-i18n-ja" */ '@atlassian/agent-global-side-navigation/i18n/ja'
		),
	ko: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/global-side-nav-agents-i18n-ko" */ '@atlassian/agent-global-side-navigation/i18n/ko'
		),
	nb: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/global-side-nav-agents-i18n-nb" */ '@atlassian/agent-global-side-navigation/i18n/nb'
		),
	nl: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/global-side-nav-agents-i18n-nl" */ '@atlassian/agent-global-side-navigation/i18n/nl'
		),
	pl: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/global-side-nav-agents-i18n-pl" */ '@atlassian/agent-global-side-navigation/i18n/pl'
		),
	'pt-BR': () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/global-side-nav-agents-i18n-pt_BR" */ '@atlassian/agent-global-side-navigation/i18n/pt_BR'
		),
	ru: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/global-side-nav-agents-i18n-ru" */ '@atlassian/agent-global-side-navigation/i18n/ru'
		),
	sv: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/global-side-nav-agents-i18n-sv" */ '@atlassian/agent-global-side-navigation/i18n/sv'
		),
	th: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/global-side-nav-agents-i18n-th" */ '@atlassian/agent-global-side-navigation/i18n/th'
		),
	tr: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/global-side-nav-agents-i18n-tr" */ '@atlassian/agent-global-side-navigation/i18n/tr'
		),
	uk: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/global-side-nav-agents-i18n-uk" */ '@atlassian/agent-global-side-navigation/i18n/uk'
		),
	vi: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/global-side-nav-agents-i18n-vi" */ '@atlassian/agent-global-side-navigation/i18n/vi'
		),
	'zh-TW': () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/global-side-nav-agents-i18n-zh_TW" */ '@atlassian/agent-global-side-navigation/i18n/zh_TW'
		),
	zh: () =>
		import(
			/* webpackChunkName: "@atlaskit-internal_@atlassian/global-side-nav-agents-i18n-zh" */ '@atlassian/agent-global-side-navigation/i18n/zh'
		),
} as const;

const fetchStudioFoundationsMessages = createFetchMessagesForLocale(
	asyncLanguages,
	defaultMessages,
);
const fetchSitePickerMessages = createFetchMessagesForLocale(sitePickerAsyncLanguages, {});
const fetchGlobalSideNavMessages = createFetchMessagesForLocale(globalSideNavAsyncLanguages, {});
const fetchConversationAssistantMessages = createFetchMessagesForLocale(
	conversationAssistantI18n.asyncLanguages,
	conversationAssistantI18n.defaultMessages,
);

export const fetchMessagesForLocale = async (locale: string): Promise<I18NMessages> => {
	const [
		studioFoundations,
		sitePicker,
		globalSideNav,
		conversationAssistant,
		appBuilderStudio,
		agentStudio,
		agentStudioPermissionSettings,
		solutionArchitectUiComponents,
		solutionTemplates,
	] = await Promise.allSettled([
		fetchStudioFoundationsMessages(locale),
		fetchSitePickerMessages(locale),
		fetchGlobalSideNavMessages(locale),
		fetchConversationAssistantMessages(locale),
		fetchAppBuilderStudioLocale(locale),
		fetchAgentStudioLocale(locale),
		fetchAgentStudioPermissionSettingsLocale(locale),
		fetchSolutionArchitectUiComponentsLocale(locale),
		fetchSolutionTemplatesLocale(locale),
	]);

	return Object.assign(
		{},
		studioFoundations.status === 'fulfilled' ? studioFoundations.value : defaultMessages,
		sitePicker.status === 'fulfilled' ? sitePicker.value : {},
		globalSideNav.status === 'fulfilled' ? globalSideNav.value : {},
		conversationAssistant.status === 'fulfilled'
			? conversationAssistant.value
			: conversationAssistantI18n.defaultMessages,
		appBuilderStudio.status === 'fulfilled' ? appBuilderStudio.value : {},
		agentStudio.status === 'fulfilled' ? agentStudio.value : {},
		agentStudioPermissionSettings.status === 'fulfilled' ? agentStudioPermissionSettings.value : {},
		solutionArchitectUiComponents.status === 'fulfilled' ? solutionArchitectUiComponents.value : {},
		solutionTemplates.status === 'fulfilled' ? solutionTemplates.value : {},
	) as I18NMessages;
};

export { defaultMessages };
